import React, { useState, useEffect } from 'react';
import KickImage from 'Reuseables/KickImage';
import { connect } from 'react-redux';
import { YearPicker, MonthPicker, DayPicker } from 'react-dropdown-date';
import { DropzoneRootProps, useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import moment from 'moment';
import DEFAULT_PROFILE from '../../../images/profile-default.png';

// ========= action import section ========= //
import { uploadImageToS3 } from '../../../actions/dashboardAction';
import {
  doFetchProfile,
  doUpdateProfile,
  doUpdateUserProfile,
} from '../../../actions/authAction';
// ====== end of action import section ====== //

// ========= interface import section ========= //
import AuthInterface, {
  UserDataInterface,
} from '../../../interface/reducers/auth';
import { Col, Row } from 'react-bootstrap';
import { parsePhone } from '../../../utils';
// ====== end of interface import section ====== //

type stateToProps = {
  dataAuth: AuthInterface;
};

interface PersonalProfileInterface {
  // state to props
  userData: UserDataInterface;

  // dispatch to props
  doSaveImageUrl: (file: FormData) => Promise<any>;
  doUpdateMyProfile: (params: any) => Promise<any>;
  doUpdateMyUserData: (params: any) => Promise<any>;
  doGetProfile: () => Promise<any>;
}

/**
 * Personal Profile Tab on Setting User Dashboard section for Kick Avenue 3.0 Website.
 * ===============================================
 * @param {PersonalProfileInterface} props for this component.
 * @return {JSX.Element}
 */
function PersonalProfileTab({
  userData,
  doSaveImageUrl,
  doUpdateMyProfile,
  doUpdateMyUserData,
  doGetProfile,
}: PersonalProfileInterface): JSX.Element {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState('');
  const [disabledButton, setDisabledButton] = useState(true);
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    onDrop: (acceptedFiles: File[]) => {
      const formData = new FormData();
      formData.append('name', 'profile-picture/');
      formData.append('file', acceptedFiles[0]);
      doSaveImageUrl(formData).then((res) => {
        setSelectedPhoto(res.url);
      });
    },
  });
  const componentProps: DropzoneRootProps = getRootProps({
    className: 'button-upload',
  });

  const onChangeFirstName = (e: any) => setFirstName(e.target.value);
  const onChangeLastName = (e: any) => setLastName(e.target.value);

  useEffect(() => {
    let uDay = '';
    let uMon = '';
    let uYear = '';
    if (userData.birthed_at) {
      const birthday = moment(userData.birthed_at).format('YYYY-MM-DD');
      const dob = birthday.split('-', 3);
      uDay = parsePhone(dob[2]);
      let mon = parseInt(dob[1]) - 1;
      uMon = parsePhone(mon.toString());
      uYear = dob[0];
    }

    if (
      firstName === '' ||
      // lastName === '' ||
      day === '' ||
      month === '' ||
      year === '' ||
      (firstName === userData.first_name &&
        lastName === userData.family_name &&
        day === uDay &&
        month === uMon &&
        year === uYear &&
        selectedPhoto === userData.img_url)
    ) {
      setDisabledButton(true);
    } else {
      setDisabledButton(false);
    }
  }, [firstName, lastName, day, month, year, selectedPhoto]);

  useEffect(() => {
    if (userData) {
      setFirstName(userData.first_name);
      setLastName(userData.family_name);
      setSelectedPhoto(userData.img_url);
      if (userData.birthed_at !== null) {
        const birthday = moment(userData.birthed_at).format('YYYY-MM-DD');
        const dob = birthday.split('-', 3);
        setDay(parsePhone(dob[2]));
        let mon = parseInt(dob[1]) - 1;
        setMonth(parsePhone(mon.toString()));
        setYear(dob[0]);
      }
    }
  }, [userData]);

  useEffect(() => {
    doGetProfile();
  }, []);

  const onSubmit = () => {
    if (!disabledButton) {
      const isEmptyBirthDay =
        (day === null || day === '') &&
        (month === null || month === '') &&
        (year === null || year === '');
      const birthDate = isEmptyBirthDay
        ? null
        : year + '-' + (parseInt(month) + 1) + '-' + day;
      const payload = {
        email: userData.email,
        first_name: firstName,
        family_name: lastName,
        birthed_at: birthDate,
        img_url: selectedPhoto,
      };
      doUpdateMyProfile(payload).then(() => {
        doUpdateMyUserData(payload).then(() => {
          doGetProfile();
          setDisabledButton(true);
          toast.success('Success updating your personal profile.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
          });
        });
      });
    }
  };

  return (
    <div className={'personal-profile-container'}>
      <div className={'photo-container'}>
        <KickImage
          src={
            selectedPhoto === '' || !selectedPhoto
              ? DEFAULT_PROFILE
              : selectedPhoto
          }
        />
        <div {...componentProps}>
          <input {...getInputProps()} />
          <p>Upload new Picture</p>
        </div>
      </div>
      <div className={'form-container'}>
        <div className={'form-parent'}>
          <p>First Name</p>
          <input
            placeholder={'Enter your First Name'}
            onChange={onChangeFirstName}
            value={firstName}
          />
        </div>
        <div className={'form-parent'}>
          <p>Last Name</p>
          <input
            placeholder={'Enter your Last Name'}
            onChange={onChangeLastName}
            value={lastName}
          />
        </div>
        <div className={'form-parent'}>
          <p>Date Of Birth</p>
          <Row>
            <Col
              md={4}
              style={{ marginBottom: window.screen.availWidth < 480 && '16px' }}
            >
              <div className="select-wrapper">
                <DayPicker
                  defaultValue={'Select Date'}
                  year={year}
                  month={month}
                  endYearGiven
                  required={true}
                  value={day}
                  onChange={(d) => {
                    setDay(d);
                  }}
                  id={'day'}
                  name={'day'}
                />
              </div>
            </Col>
            <Col
              md={4}
              style={{ marginBottom: window.screen.availWidth < 480 && '16px' }}
            >
              <div className="select-wrapper">
                <MonthPicker
                  defaultValue={'Select Month'}
                  endYearGiven
                  year={year}
                  required={true}
                  value={month}
                  onChange={(m) => {
                    setMonth(m);
                  }}
                  id={'month'}
                  name={'month'}
                />
              </div>
            </Col>
            <Col md={4}>
              <div className="select-wrapper">
                <YearPicker
                  defaultValue={'Select Year'}
                  start={1901}
                  reverse
                  required={true}
                  value={year}
                  onChange={(y) => {
                    setYear(y);
                  }}
                  id={'year'}
                  name={'year'}
                />
              </div>
            </Col>
          </Row>
        </div>
        <div
          className={`button-submit ${disabledButton ? 'disabled' : ''}`}
          onClick={onSubmit}
        >
          <p>Submit</p>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state: stateToProps) => {
  return {
    userData: state.dataAuth.userdata,
  };
};

const mapDispatchToProps = (dispatch: (arg0: any) => any) => {
  return {
    doSaveImageUrl: (file: FormData) => uploadImageToS3(file),
    doUpdateMyProfile: (params: any) => dispatch(doUpdateProfile(params)),
    doUpdateMyUserData: (params: any) => dispatch(doUpdateUserProfile(params)),
    doGetProfile: () => dispatch(doFetchProfile()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PersonalProfileTab);
